/**
 * WalletListener
 *
 * Listens for SecureStorage changes to the wallet via eventBus and updates
 * Redux state accordingly (mirrors Flutter's WalletNotifier stream
 * subscription pattern), and additionally starts the Firestore live listener
 * that Flutter starts at app boot via `WalletService.listenToWalletChanges`
 * (lib/services/wallet_service.dart:13-29), invoked from
 * `MainScreen._initWallet` (lib/presentation/screens/main/main_screen.dart:197,
 * 228-232) — that listener is the source of the SecureStorage writes this
 * component mirrors into Redux.
 *
 * Renders nothing — mount alongside AppInitializer in the app tree.
 *
 * Why `eventBus.on` directly (vs. `useListenToSecureStorageUpdates`):
 * The event payload is discarded — we use the storage write only as a "wallet
 * changed, refresh" signal and call `GetWalletUseCase` to re-aggregate. The
 * storage hook would maintain React state (`data`) we never read. Compare to
 * AuthUserListener, where the storage value IS the source of truth and the
 * hook's typed `data` is consumed directly.
 *
 * Startup race: not exposed here, but only because this listener does NOT
 * perform an initial storage read on mount — it just registers an
 * `eventBus.on` subscription, and writes (which trigger the eventBus) can
 * only happen after storage is initialized. Note that `isAuthenticated`
 * flipping to true does NOT imply storage is initialized: on fresh sign-in,
 * RootScreen's onAuthStateChanged dispatches setFirebaseUserId before
 * LoginPage completes `initializeSecureStorageUseCase` (LoginPage arms
 * `isInitializing` to bridge this gap — see ExclusiveContentListener /
 * UserDeviceListener for the gate pattern). If anyone later adds an initial
 * `reloadWallet()` call inside the eventBus effect below, also gate on
 * `!isInitializing`. The Firestore live-subscribe effect already follows that
 * rule.
 */
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '@/presentation/store';
import { setWallet, clearWallet } from '@/presentation/store/slices/wallet/walletSlice';
import { GetWalletUseCase } from '@/domain/usecases/wallet/getWalletUseCase';
import { subscribeToWalletLiveChangesUseCase } from '@/domain/usecases/wallet/subscribeToWalletLiveChangesUseCase';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import { eventBus } from '@/infrastructure/eventBus';

export const WalletListener = () => {
  const dispatch = useDispatch<AppDispatch>();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const firebaseUserId = useSelector((state: RootState) => state.auth.firebaseUserId);
  const isInitializing = useSelector((state: RootState) => state.auth.isInitializing);

  // Clear wallet when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      dispatch(clearWallet());
    }
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const reloadWallet = async () => {
      const useCase = new GetWalletUseCase();
      const wallet = await useCase.execute();
      dispatch(setWallet(wallet));
    };

    // Subscribe to SecureStorage changes for the wallet key.
    // When a change is detected (e.g. after a sync), reload wallet into Redux.
    const unsubscribe = eventBus.on(SECURE_STORAGE_KEYS.WALLET, () => {
      reloadWallet();
    });

    return () => {
      unsubscribe();
    };
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    if (!isAuthenticated || !firebaseUserId || isInitializing) return;

    const unsubscribe = subscribeToWalletLiveChangesUseCase.execute(firebaseUserId);

    return unsubscribe;
  }, [isAuthenticated, firebaseUserId, isInitializing]);

  return null;
};
